// PHA-1168: Clinic Settings > Views. A clinic admin's list of saved Patient List
// and Billing Report views: open one in the builder, start a new one, choose the
// clinic default, archive.
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertDialog, Badge, Button, Callout, Card, Flex, Table, Tabs, Text,
} from '@radix-ui/themes';
import { InfoCircledIcon, PlusIcon } from '@radix-ui/react-icons';
import { toast } from 'react-hot-toast';
import ClinicLayout from './ClinicLayout';
import { useOrganization } from '../../contexts/OrganizationContext';
import { customViewsApi, CustomViewSummary, CustomViewSurface, customViewErrorDetails } from '../../services/api';
import { useCustomViews } from '../customViews/useCustomViews';

const SURFACES: { key: CustomViewSurface; label: string }[] = [
  { key: 'patient_list', label: 'Patient List' },
  { key: 'billing', label: 'Billing Report' },
];

const ClinicViewsPage: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const surface: CustomViewSurface = params.get('surface') === 'billing' ? 'billing' : 'patient_list';
  const { orgId, orgName } = useOrganization();
  const views = useCustomViews(orgId, surface);
  const [busy, setBusy] = useState<number | 'clear' | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<CustomViewSummary | null>(null);

  const run = async (key: number | 'clear', fn: () => Promise<unknown>, done: string) => {
    setBusy(key);
    try {
      await fn();
      toast.success(done);
      views.refresh();
    } catch (err) {
      toast.error(customViewErrorDetails(err).join(' '));
    } finally {
      setBusy(null);
    }
  };

  const own = (v: CustomViewSummary) => !v.inherited;

  let body: React.ReactNode;
  if (orgId === 'ALL' || !orgId) {
    body = (
      <Callout.Root>
        <Callout.Icon><InfoCircledIcon /></Callout.Icon>
        <Callout.Text>Pick a clinic to manage its views.</Callout.Text>
      </Callout.Root>
    );
  } else if (views.loading && !views.views.length) {
    body = <Text color="gray">Loading views…</Text>;
  } else if (!views.enabled) {
    body = (
      <Callout.Root>
        <Callout.Icon><InfoCircledIcon /></Callout.Icon>
        <Callout.Text>Custom views are not turned on for {orgName || 'this clinic'} yet. Contact Phanova support to enable them.</Callout.Text>
      </Callout.Root>
    );
  } else if (!views.canDesign) {
    body = (
      <Callout.Root color="orange">
        <Callout.Icon><InfoCircledIcon /></Callout.Icon>
        <Callout.Text>Only clinic admins can design views. You can still pick a saved view on the {surface === 'billing' ? 'Billing Report' : 'Patient List'}.</Callout.Text>
      </Callout.Root>
    );
  } else {
    const anyOwnDefault = views.views.some((v) => own(v) && v.is_default);
    body = (
      <Flex direction="column" gap="3">
        <Flex justify="between" align="center">
          <Text size="2" color="gray">
            Everyone at {orgName || 'this clinic'} can switch between these views. The default opens first.
          </Text>
          <Button onClick={() => navigate(`/clinic-settings/views/new?surface=${surface}`)}>
            <PlusIcon /> New view
          </Button>
        </Flex>
        <Card>
          <Table.Root size="2">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Updated</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell justify="end">Actions</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              <Table.Row>
                <Table.Cell><Text weight="medium">Phanova Standard</Text></Table.Cell>
                <Table.Cell>
                  {!views.views.some((v) => v.effective_default) && <Badge color="green">Default</Badge>}
                </Table.Cell>
                <Table.Cell><Text color="gray" size="2">Built in</Text></Table.Cell>
                <Table.Cell justify="end">
                  {anyOwnDefault && (
                    <Button size="1" variant="soft" disabled={busy !== null}
                      onClick={() => run('clear', () => customViewsApi.setDefault(orgId, surface, null), 'Phanova Standard is the default')}>
                      Make default
                    </Button>
                  )}
                </Table.Cell>
              </Table.Row>
              {views.views.map((v) => (
                <Table.Row key={v.id}>
                  <Table.Cell>
                    <Flex direction="column">
                      <Text weight="medium">{v.name}</Text>
                      {v.description && <Text size="1" color="gray">{v.description}</Text>}
                    </Flex>
                  </Table.Cell>
                  <Table.Cell>
                    <Flex gap="1" wrap="wrap">
                      {v.effective_default && <Badge color="green">Default</Badge>}
                      {v.inherited && <Badge color="blue">From {v.organization_name || 'network'}</Badge>}
                      {v.hides_billable_rows && <Badge color="amber">Hides billable rows</Badge>}
                      {v.flagged_stale && <Badge color="red">Needs re-save</Badge>}
                    </Flex>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="2" color="gray">{v.updated_ts ? new Date(v.updated_ts).toLocaleDateString() : '—'}</Text>
                  </Table.Cell>
                  <Table.Cell justify="end">
                    <Flex gap="2" justify="end">
                      <Button size="1" variant="soft" onClick={() => navigate(`/clinic-settings/views/${v.id}`)}>
                        {own(v) ? 'Edit' : 'Open'}
                      </Button>
                      {own(v) && !v.is_default && (
                        <Button size="1" variant="soft" disabled={busy !== null}
                          onClick={() => run(v.id, () => customViewsApi.setDefault(orgId, surface, v.id), `"${v.name}" is now the default`)}>
                          Make default
                        </Button>
                      )}
                      {own(v) && (
                        <Button size="1" variant="soft" color="red" disabled={busy !== null}
                          onClick={() => setArchiveTarget(v)}>
                          Archive
                        </Button>
                      )}
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Card>
      </Flex>
    );
  }

  return (
    <ClinicLayout title="Views" subtitle="Design how your team sees the Patient List and the Billing Report.">
      <Tabs.Root value={surface} onValueChange={(v) => setParams({ surface: v })}>
        <Tabs.List>
          {SURFACES.map((s) => <Tabs.Trigger key={s.key} value={s.key}>{s.label}</Tabs.Trigger>)}
        </Tabs.List>
      </Tabs.Root>
      <div className="mt-4">{body}</div>

      <AlertDialog.Root open={!!archiveTarget} onOpenChange={(open) => { if (!open) setArchiveTarget(null); }}>
        <AlertDialog.Content style={{ maxWidth: 440 }}>
          <AlertDialog.Title>Archive "{archiveTarget?.name}"?</AlertDialog.Title>
          <AlertDialog.Description size="2">
            Your team will no longer see this view. Anyone using it switches to the clinic default.
          </AlertDialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel><Button variant="soft" color="gray">Cancel</Button></AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button color="red" onClick={() => {
                const target = archiveTarget;
                if (target) void run(target.id, () => customViewsApi.archive(orgId, target.id), `"${target.name}" archived`);
              }}>Archive</Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </ClinicLayout>
  );
};

export default ClinicViewsPage;
